import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import {
  GETTING_STARTED_STEPS,
  isGettingStartedStepComplete,
  useHomeActivation,
} from "@/home"
import {
  Button,
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from "@coval/ui"
import { CheckCircle2, Circle } from "lucide-react"

import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"

const log = logger("GettingStartedWidget")

interface GettingStartedWidgetProps {
  orgSlug: string
}

export function GettingStartedWidget({ orgSlug }: GettingStartedWidgetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [storageLoaded, setStorageLoaded] = useState(false)
  const loggedConfigCountsErrorRef = useRef(false)
  const router = useRouter()
  const queryParams = useQueryParams()

  const {
    data: counts,
    error: configCountsError,
    isLoading,
    isError,
  } = useHomeActivation()

  useEffect(() => {
    setStorageLoaded(true)
  }, [])

  useEffect(() => {
    if (!isError || loggedConfigCountsErrorRef.current) {
      return
    }

    loggedConfigCountsErrorRef.current = true
    log.error(
      "GettingStartedWidget: failed to load config counts",
      configCountsError,
    )
  }, [configCountsError, isError])

  const tasks = GETTING_STARTED_STEPS.map((step) => ({
    id: step.id,
    label: step.label,
    completed: isGettingStartedStepComplete(step, counts),
    href: queryParams.getHref(step.path, orgSlug),
  }))

  const sortedTasks = [...tasks].sort(
    (a, b) => Number(a.completed) - Number(b.completed),
  )
  const completedCount = tasks.filter((t) => t.completed).length
  const allDone = completedCount === tasks.length

  if (!storageLoaded || isLoading) {
    return null
  }

  if (isError) {
    return null
  }

  if (!counts || allDone) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className="w-full justify-between items-center h-[28px] px-2 hover:bg-surface-0-hover"
          aria-label="Toggle getting started checklist"
        >
          <span className="text-sm font-medium text-coval-text">
            Get started
          </span>
          <span className="text-sm text-coval-text-muted">
            {completedCount}/{tasks.length}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2 shadow-none" side="top" align="start">
        <div className="flex flex-col gap-1">
          <p className="px-2 py-1 text-sm font-semibold text-coval-text">
            Get started
          </p>
          {sortedTasks.map((task) => (
            <PopoverItem
              key={task.id}
              aria-label={task.label}
              disabled={task.completed}
              className={task.completed ? "opacity-50" : undefined}
              onClick={() => {
                void router.push(task.href)
                setIsOpen(false)
              }}
            >
              {task.completed ? (
                <CheckCircle2
                  className="size-3.5 shrink-0 text-coval-text-muted"
                  aria-hidden
                />
              ) : (
                <Circle
                  className="size-3.5 shrink-0 text-coval-text-muted"
                  aria-hidden
                />
              )}
              <span className={task.completed ? "line-through" : undefined}>
                {task.label}
              </span>
            </PopoverItem>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
